import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download } from 'lucide-react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const STATUS_STYLE = { success: 'bg-emerald-50 text-emerald-700 border-emerald-200', failed: 'bg-red-50 text-red-700 border-red-200', pending: 'bg-amber-50 text-amber-700 border-amber-200' };

export const PaymentsHistory = () => {
  const [rows, setRows] = useState(null);
  const load = () => axios.get(`${API}/billing/payments`, { headers: headers() }).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => {
    load();
    window.addEventListener('bc:credits-changed', load);
    return () => window.removeEventListener('bc:credits-changed', load);
  }, []);
  const download = async (r) => {
    try {
      const res = await axios.get(`${API}/billing/receipts/${r.receipt_id}/pdf`, { headers: headers(), responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.data);
      a.download = `Invoice_${r.receipt_id}.pdf`;
      a.click();
    } catch {
      toast.error('Could not download invoice');
    }
  };
  if (rows === null) return <div className="h-16 rounded-xl bg-stone-100 animate-pulse" />;
  if (!rows.length) return <p className="text-sm text-stone-500" data-testid="payments-history-empty">No payments yet. Every subscription and top-up payment will appear here.</p>;
  return (
    <div className="overflow-x-auto" data-testid="payments-history-table">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[11px] uppercase tracking-wide text-stone-500 border-b">
          <th className="py-2 pr-3">Date</th><th className="py-2 pr-3">Type</th><th className="py-2 pr-3">Details</th>
          <th className="py-2 pr-3 text-right">Amount</th><th className="py-2 pr-3">Razorpay ID</th><th className="py-2 pr-3">Status</th><th className="py-2">Invoice</th>
        </tr></thead>
        <tbody className="divide-y divide-stone-100">
          {rows.map((r) => (
            <tr key={r.id} data-testid={`payment-row-${r.id}`}>
              <td className="py-2.5 pr-3 whitespace-nowrap text-stone-600">{new Date(r.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
              <td className="py-2.5 pr-3 capitalize">{r.kind === 'topup' ? 'Top-up' : 'Subscription'}</td>
              <td className="py-2.5 pr-3 text-stone-700">{r.purpose}{r.user_email ? <span className="block text-[11px] text-stone-400">{r.user_email}</span> : null}</td>
              <td className="py-2.5 pr-3 text-right font-mono font-semibold">₹{Number(r.amount || 0).toLocaleString('en-IN')}</td>
              <td className="py-2.5 pr-3 font-mono text-xs text-stone-500">{r.payment_id || '—'}</td>
              <td className="py-2.5 pr-3"><Badge variant="outline" className={`capitalize ${STATUS_STYLE[r.status] || ''}`} data-testid={`payment-status-${r.id}`}>{r.status}</Badge></td>
              <td className="py-2.5">
                {r.status === 'success' && r.receipt_id ? (
                  <Button size="sm" variant="outline" onClick={() => download(r)} data-testid={`payment-invoice-${r.id}`}><Download size={13} className="mr-1" /> PDF</Button>
                ) : <span className="text-stone-300">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
